import { withRub } from '@constants/currency'
import { handleUnauthorized } from '@utils/apiHelpers'
import { t } from '@store/I18nStore'
import { monthsShort, timeUnit } from '@utils/dateTime'

export const BILLING_ENTITLEMENTS_PATH = '/api/billing/entitlements'
export const BILLING_PLANS_PATH = '/api/billing/plans'
export const BILLING_SUBSCRIPTION_CHANGE_PATH = '/api/billing/subscription/change'
export const BILLING_SUBSCRIPTION_CANCEL_AT_PERIOD_END_PATH =
  '/api/billing/subscription/cancel-at-period-end'
export const BILLING_INVOICES_PATH = '/api/billing/invoices'

export type BillingSubscriptionStatus =
  | 'TRIAL'
  | 'ACTIVE'
  | 'PAST_DUE'
  | 'CANCELLED'
  | 'EXPIRED'
  | string

export type BillingPlanKind = 'TRIAL' | 'PAID' | string

export type BillingCustomizationLevel = 'BASIC' | 'FULL' | string

export type BillingUsageCounter = {
  used: number
  limit: number | null
  remaining: number | null
  reached: boolean
  unlimited: boolean
}

export type BillingRegionUsageItem = BillingUsageCounter & {
  channelId: number
  channelName: string
  widgetId: number
  widgetKey: string
}

export type BillingUser = {
  id: number
  role: string
  ownerId: number | null
  emailVerified?: boolean
}

export type BillingSubscription = {
  id: string
  status: BillingSubscriptionStatus
  active: boolean
  startedAt: string | null
  currentPeriodStart: string | null
  currentPeriodEnd: string | null
  trialEndsAt: string | null
  accessUntil: string | null
  autoRenew: boolean
  cancelAtPeriodEnd: boolean
  cancelledAt: string | null
  planId: number | null
  operatorSeats: number | null
  periodMonths: number | null
}

export type BillingPlan = {
  id: number
  code: string
  name: string
  kind: BillingPlanKind
  periodMonths: number
  unitPriceCents: number
  discountBps: number
  periodAmountCents: number
  operatorSeats: number
}

export type BillingPlanPeriod = {
  id: number | null
  periodMonths: number
  label: string
  discountBps: number
  unitPriceCents: number | null
  periodAmountCents: number | null
  planId: number | null
}

export type BillingCatalogPlan = {
  id: number
  code: string
  name: string
  kind: BillingPlanKind
  description: string
  unitPriceCents: number
  discountBps: number
  periodMonths: number
  periodAmountCents: number
  defaultOperatorSeats: number
  popular: boolean
  sortOrder: number
  hasLimits: boolean
  limits: BillingLimits
  capabilities: BillingCapabilities
  periods: BillingPlanPeriod[]
}

export type BillingPlansCatalog = {
  currency: string
  plans: BillingCatalogPlan[]
  periods: BillingPlanPeriod[]
}

export type BillingSubscriptionChangeRequest = {
  planId: number
  operatorSeats: number
  periodMonths: number
  /**
   * true — продление текущего тарифа (planId/operatorSeats/periodMonths строго текущие).
   * false — смена тарифа / операторов / периода (при выборе нового тарифа renew:true нельзя).
   */
  renew?: boolean
}

export type BillingChangeStatus =
  | 'QUOTED'
  | 'PENDING_PAYMENT'
  | 'APPLIED'
  | 'CANCELLED'
  | 'EXPIRED'
  | string

export type BillingInvoiceStatus =
  | 'DRAFT'
  | 'PENDING'
  | 'PAID'
  | 'OVERDUE'
  | 'CANCELLED'
  | 'REFUNDED'
  | 'PARTIALLY_REFUNDED'
  | string

export type BillingInvoiceType =
  | 'NEW_SUBSCRIPTION'
  | 'RENEWAL'
  | 'PLAN_CHANGE'
  | 'SEAT_CHANGE'
  | 'CANCELLATION'
  | 'ADJUSTMENT'
  | string

export type BillingPaymentStatus = 'PENDING' | 'SUCCEEDED' | 'FAILED' | 'CANCELLED' | string

export type BillingPlanSnapshot = {
  planId: number
  planCode: string
  planName: string
  operatorSeats: number
  periodMonths: number
  unitPriceCents: number
  discountBps: number
  subtotalCents: number
  discountCents: number
  periodAmountCents: number
  currency: string
}

export type BillingInvoiceSummary = {
  id: string
  number: string
  status: BillingInvoiceStatus
  type: BillingInvoiceType
  currency: string
  totalCents: number
  amountDueCents: number
  dueAt: string | null
  createdAt: string | null
  paidAt: string | null
}

export type BillingSubscriptionChange = {
  changeId: string
  subscriptionId: string | null
  invoiceId: string | null
  status: BillingChangeStatus
  quoteExpired: boolean
  from: BillingPlanSnapshot | null
  to: BillingPlanSnapshot | null
  creditCents: number
  amountDueCents: number
  refundDueCents: number
  paymentRequired: boolean
  refundRequired: boolean
  expiresAt: string | null
  createdAt: string | null
  appliedAt: string | null
  invoice: BillingInvoiceSummary | null
}

export type BillingQuote = {
  unitPriceCents: number
  discountBps: number
  monthlyPerOperatorCents: number
  monthlyCents: number
  periodMonths: number
  operatorSeats: number
  totalCents: number
  saveCents: number
}

export type BillingPagination = {
  page: number
  perPage: number
  total: number
  totalPages: number
  hasNextPage: boolean
}

export type BillingInvoiceListItem = {
  id: string
  number: string
  status: BillingInvoiceStatus
  type: BillingInvoiceType
  currency: string
  subtotalCents: number
  discountCents: number
  totalCents: number
  creditCents: number
  amountDueCents: number
  dueAt: string | null
  issuedAt: string | null
  paidAt: string | null
  createdAt: string | null
  planCode: string
  planName: string
  operatorSeats: number
  periodMonths: number
  unitPriceCents: number
}

export type BillingInvoiceLineItem = {
  id: string
  description: string
  quantity: number
  unitPriceCents: number
  amountCents: number
}

export type BillingPayment = {
  paymentId: string
  invoiceId: string
  provider: string
  method: string
  /** Код опции оплаты (`paymentOption`), которой была сделана попытка. */
  option: string
  /** Метод для отображения, если бэк его отдаёт (CARD / SBP / CRYPTO / …). */
  displayMethod: string | null
  status: BillingPaymentStatus
  amountCents: number
  currency: string
  checkoutUrl: string | null
  paidAt: string | null
  createdAt: string | null
  invoice: BillingInvoiceSummary | null
  change: BillingSubscriptionChange | null
}

/** Known payment providers; other codes allowed. */
export type BillingPaymentProviderCode = 'payou' | 'kassify' | (string & {})

/** Calculated pay option from `GET .../payment-options`. */
export type BillingInvoicePaymentOption = {
  provider: BillingPaymentProviderCode
  option: string
  title: string
  method: string
  currency: string
  unitsPerUsd: string
  decimals: number
  invoiceAmountCents: number
  feeCents: number
  totalCents: number
  payAmount: string
  feePayer: string
  settingsVersion: number
  displayMethod?: string | null
  displayGroup?: string | null
  icon?: string | null
  position?: number
}

/** `GET /api/billing/invoices/{id}/payment-options` */
export type BillingInvoicePaymentOptions = {
  invoiceId: string
  currency: string
  amountCents: number
  options: BillingInvoicePaymentOption[]
}

/** `POST /api/billing/invoices/{id}/payments` body */
export type CreateInvoicePaymentBody = {
  provider: BillingPaymentProviderCode
  option: string
  settingsVersion: number
}

export type BillingPaymentsBlock = {
  items: BillingPayment[]
  total: number
  limit: number
  hasMore: boolean
  pagination: BillingPagination
}

export type BillingPaymentsList = {
  items: BillingPayment[]
  pagination: BillingPagination
}

export type BillingInvoiceDetail = BillingInvoiceListItem & {
  billingProfileSnapshot: Record<string, string>
  items: BillingInvoiceLineItem[]
  payments: BillingPaymentsBlock
  change: BillingSubscriptionChange | null
}

export type BillingInvoiceList = {
  items: BillingInvoiceListItem[]
  pagination: BillingPagination
}

export type BillingInvoiceListQuery = {
  page?: number
  perPage?: number
  status?: BillingInvoiceStatus
  type?: BillingInvoiceType
  search?: string
}

export type BillingPaymentsListQuery = {
  page?: number
  perPage?: number
}

function defaultPeriodLabel(months: number): string {
  if (months === 12) return t('time.periods.year')
  return monthsShort(months)
}

/** Fallback period discounts when GET /plans does not return a periods list (10% / 15% / 30%). */
export const DEFAULT_BILLING_PERIODS: BillingPlanPeriod[] = [
  { id: null, periodMonths: 1, label: monthsShort(1), discountBps: 0, unitPriceCents: null, periodAmountCents: null, planId: null },
  { id: null, periodMonths: 3, label: monthsShort(3), discountBps: 1000, unitPriceCents: null, periodAmountCents: null, planId: null },
  { id: null, periodMonths: 6, label: monthsShort(6), discountBps: 1500, unitPriceCents: null, periodAmountCents: null, planId: null },
  { id: null, periodMonths: 12, label: t('time.periods.year'), discountBps: 3000, unitPriceCents: null, periodAmountCents: null, planId: null },
]

export type BillingLimits = {
  operatorSeats: number | null
  channelLimit: number | null
  regionLimit: number | null
  commonTemplateSectionLimit: number | null
  personalTemplateSectionLimit: number | null
  personalTemplatesEnabled: boolean
  customizationLevel: BillingCustomizationLevel
  canRemoveBranding: boolean
}

export type BillingUsage = {
  operatorSeats: BillingUsageCounter
  channels: BillingUsageCounter
  regions: {
    limitPerChannel: number | null
    items: BillingRegionUsageItem[]
  }
  templateSections: {
    common: BillingUsageCounter
    personal: BillingUsageCounter
  }
}

export type BillingCapabilities = {
  personalTemplates: boolean
  createCommonTemplateSections: boolean
  customizationLevel: BillingCustomizationLevel
  removeBranding: boolean
}

export type BillingAccessState = string

export type BillingAccess = {
  state: BillingAccessState
  reason: string | null
}

export type BillingPermissions = {
  canUseWorkspace: boolean
  canManageBilling: boolean
}

export type BillingOwner = {
  id: number
  email: string
  displayName: string
}

export type BillingEntitlements = {
  generatedAt: string
  user: BillingUser
  access: BillingAccess | null
  owner: BillingOwner | null
  permissions: BillingPermissions
  /** Present only when the account exposes billing details (typically canManageBilling). */
  subscription: BillingSubscription | null
  plan: BillingPlan | null
  limits: BillingLimits | null
  usage: BillingUsage | null
  capabilities: BillingCapabilities | null
}

type ApiSuccess<T> = {
  success?: boolean
  data?: T
  error?: string
  message?: string
  code?: string
  details?: unknown
}

export class BillingApiError extends Error {
  status: number
  code?: string
  details?: unknown

  constructor(message: string, status: number, code?: string, details?: unknown) {
    super(message)
    this.name = 'BillingApiError'
    this.status = status
    this.code = code
    this.details = details
  }
}

function trimBase(apiBaseUrl: string): string {
  return apiBaseUrl.replace(/\/$/, '')
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' ? (value as Record<string, unknown>) : null
}

function asString(value: unknown, fallback = ''): string {
  return typeof value === 'string' ? value : fallback
}

function asNullableString(value: unknown): string | null {
  if (value == null) return null
  return typeof value === 'string' ? value : null
}

function asNumber(value: unknown, fallback = 0): number {
  const n = Number(value)
  return Number.isFinite(n) ? n : fallback
}

function asNullableNumber(value: unknown): number | null {
  if (value == null) return null
  const n = Number(value)
  return Number.isFinite(n) ? n : null
}

function positiveInt(value: unknown): number | null {
  const n = Math.trunc(asNumber(value, 0))
  return n > 0 ? n : null
}

function asBoolean(value: unknown, fallback = false): boolean {
  return typeof value === 'boolean' ? value : fallback
}

function asOptionalBoolean(value: unknown): boolean | undefined {
  return typeof value === 'boolean' ? value : undefined
}

function parseUsageCounter(
  raw: unknown,
  fallbackUsed = 0,
): BillingUsageCounter {
  const rec = asRecord(raw)
  const limit = asNullableNumber(rec?.limit)
  const unlimited = asBoolean(rec?.unlimited, limit == null)
  const used = asNumber(rec?.used, fallbackUsed)
  const remaining =
    rec?.remaining === undefined ? (unlimited || limit == null ? null : Math.max(0, limit - used)) : asNullableNumber(rec.remaining)
  const reached = asBoolean(rec?.reached, !unlimited && limit != null && used >= limit)
  return { used, limit, remaining, reached, unlimited }
}

function parseRegionUsageItem(raw: unknown): BillingRegionUsageItem | null {
  const rec = asRecord(raw)
  if (!rec) return null
  const counter = parseUsageCounter(rec)
  return {
    ...counter,
    channelId: asNumber(rec.channelId),
    channelName: asString(rec.channelName),
    widgetId: asNumber(rec.widgetId, asNumber(rec.channelId)),
    widgetKey: asString(rec.widgetKey),
  }
}

const LIMIT_FIELD_KEYS = [
  'operatorSeats',
  'maxOperatorSeats',
  'channelLimit',
  'regionLimit',
  'commonTemplateSectionLimit',
  'personalTemplateSectionLimit',
  'personalTemplatesEnabled',
  'customizationLevel',
  'canRemoveBranding',
] as const

function hasExplicitLimits(raw: unknown): boolean {
  const rec = asRecord(raw)
  if (!rec) return false
  return LIMIT_FIELD_KEYS.some((key) => key in rec)
}

function parseBillingLimits(raw: unknown, capabilitiesRaw?: unknown): BillingLimits {
  const limits = asRecord(raw) ?? {}
  const capabilities = asRecord(capabilitiesRaw) ?? {}
  const customizationLevel = asString(
    capabilities.customizationLevel ?? limits.customizationLevel,
    'BASIC',
  )
  return {
    operatorSeats: asNullableNumber(limits.operatorSeats ?? limits.maxOperatorSeats),
    channelLimit: asNullableNumber(limits.channelLimit),
    regionLimit: asNullableNumber(limits.regionLimit),
    commonTemplateSectionLimit: asNullableNumber(limits.commonTemplateSectionLimit),
    personalTemplateSectionLimit: asNullableNumber(limits.personalTemplateSectionLimit),
    personalTemplatesEnabled: asBoolean(
      limits.personalTemplatesEnabled ?? capabilities.personalTemplates,
    ),
    customizationLevel,
    canRemoveBranding: asBoolean(limits.canRemoveBranding ?? capabilities.removeBranding),
  }
}

function parseBillingCapabilities(raw: unknown, limits: BillingLimits): BillingCapabilities {
  const capabilities = asRecord(raw) ?? {}
  return {
    personalTemplates: asBoolean(capabilities.personalTemplates, limits.personalTemplatesEnabled),
    createCommonTemplateSections: asBoolean(capabilities.createCommonTemplateSections, true),
    customizationLevel: asString(capabilities.customizationLevel, limits.customizationLevel),
    removeBranding: asBoolean(capabilities.removeBranding, limits.canRemoveBranding),
  }
}

function unwrapBillingData(json: unknown): unknown {
  const root = asRecord(json)
  if (!root) return json
  return root.data !== undefined ? root.data : json
}

function extractUnknownList(raw: unknown): unknown[] {
  if (Array.isArray(raw)) return raw
  const rec = asRecord(raw)
  if (!rec) return []
  for (const key of ['plans', 'items', 'tariffs', 'data']) {
    const value = rec[key]
    if (Array.isArray(value)) return value
  }
  return []
}

function parsePlanPeriod(raw: unknown, fallbackPlanId?: number): BillingPlanPeriod | null {
  if (typeof raw === 'number' && Number.isFinite(raw) && raw > 0) {
    const periodMonths = Math.trunc(raw)
    return {
      id: null,
      periodMonths,
      label: defaultPeriodLabel(periodMonths),
      discountBps: 0,
      unitPriceCents: null,
      periodAmountCents: null,
      planId: fallbackPlanId ?? null,
    }
  }
  const rec = asRecord(raw)
  if (!rec) return null
  const periodMonths = asNumber(rec.periodMonths ?? rec.months ?? rec.period, 0)
  if (periodMonths <= 0) return null
  return {
    id: asNullableNumber(rec.id),
    periodMonths,
    label: asString(rec.label, defaultPeriodLabel(periodMonths)) || defaultPeriodLabel(periodMonths),
    discountBps: asNumber(rec.discountBps ?? rec.discount_bps),
    unitPriceCents: asNullableNumber(
      rec.unitPriceCents ?? rec.monthlyOperatorPriceCents ?? rec.priceCents,
    ),
    periodAmountCents: asNullableNumber(rec.periodAmountCents ?? rec.amountCents),
    planId: asNullableNumber(rec.planId) ?? fallbackPlanId ?? null,
  }
}

function parsePlanPeriods(raw: unknown, fallbackPlanId?: number): BillingPlanPeriod[] {
  if (!Array.isArray(raw)) return []
  const map = new Map<number, BillingPlanPeriod>()
  for (const item of raw) {
    const period = parsePlanPeriod(item, fallbackPlanId)
    if (period) map.set(period.periodMonths, period)
  }
  return [...map.values()].sort((a, b) => a.periodMonths - b.periodMonths)
}

function parseCatalogPlan(raw: unknown): BillingCatalogPlan | null {
  const rec = asRecord(raw)
  if (!rec) return null
  const id = asNumber(rec.id)
  if (!id) return null

  const nestedLimits = rec.limits
  const hasLimits = hasExplicitLimits(nestedLimits) || hasExplicitLimits(rec)
  const limits = parseBillingLimits(asRecord(nestedLimits) ?? rec, rec.capabilities)
  const capabilities = parseBillingCapabilities(rec.capabilities, limits)
  const periods = parsePlanPeriods(rec.periods ?? rec.pricing ?? rec.periodOptions, id)

  return {
    id,
    code: asString(rec.code),
    name: asString(rec.name, asString(rec.code) || `Тариф ${id}`),
    kind: asString(rec.kind, asString(rec.code)),
    description: asString(rec.description ?? rec.desc ?? rec.subtitle),
    unitPriceCents: asNumber(
      rec.unitPriceCents ?? rec.monthlyOperatorPriceCents ?? rec.priceCents ?? rec.monthlyPriceCents,
    ),
    discountBps: asNumber(rec.discountBps ?? rec.discount_bps),
    periodMonths: asNumber(rec.periodMonths ?? rec.months),
    periodAmountCents: asNumber(rec.periodAmountCents ?? rec.amountCents),
    defaultOperatorSeats: Math.max(1, asNumber(rec.defaultOperatorSeats, 1)),
    popular: asBoolean(rec.popular ?? rec.isPopular ?? rec.featured),
    sortOrder: asNumber(rec.sortOrder ?? rec.sort ?? rec.order, id),
    hasLimits,
    limits,
    capabilities,
    periods,
  }
}

function mergeCatalogPlans(
  plans: BillingCatalogPlan[],
  globalPeriods: BillingPlanPeriod[],
): BillingCatalogPlan[] {
  const groups = new Map<string, BillingCatalogPlan[]>()
  for (const plan of plans) {
    const key = plan.code.trim().toUpperCase() || `id:${plan.id}`
    const list = groups.get(key) ?? []
    list.push(plan)
    groups.set(key, list)
  }

  const merged: BillingCatalogPlan[] = []
  for (const list of groups.values()) {
    const sorted = [...list].sort((a, b) => a.id - b.id)
    const base =
      sorted.find((p) => p.hasLimits) ??
      sorted.find((p) => p.unitPriceCents > 0) ??
      sorted[0]
    const periodMap = new Map<number, BillingPlanPeriod>()
    for (const period of globalPeriods) periodMap.set(period.periodMonths, { ...period })
    for (const item of sorted) {
      if (item.periods.length > 0) {
        for (const period of item.periods) {
          periodMap.set(period.periodMonths, { ...period, planId: period.planId ?? item.id })
        }
      } else if (item.periodMonths > 0) {
        periodMap.set(item.periodMonths, {
          id: null,
          periodMonths: item.periodMonths,
          label: defaultPeriodLabel(item.periodMonths),
          discountBps: item.discountBps,
          unitPriceCents: item.unitPriceCents,
          periodAmountCents: item.periodAmountCents || null,
          planId: item.id,
        })
      }
    }
    merged.push({
      ...base,
      periods: [...periodMap.values()].sort((a, b) => a.periodMonths - b.periodMonths),
    })
  }

  return merged.sort((a, b) => a.sortOrder - b.sortOrder || a.id - b.id)
}

export function parseBillingPlansCatalog(json: unknown): BillingPlansCatalog {
  const payload = unwrapBillingData(json)
  const rec = asRecord(payload)
  const list = extractUnknownList(payload)
    .map(parseCatalogPlan)
    .filter((p): p is BillingCatalogPlan => p != null)
  const globalPeriods = parsePlanPeriods(rec?.periods ?? rec?.periodOptions)
  return {
    currency: asString(rec?.currency, 'USD') || 'USD',
    plans: mergeCatalogPlans(list, globalPeriods),
    periods: globalPeriods,
  }
}

function asId(value: unknown): string {
  if (value == null || value === '') return ''
  return String(value)
}

function parsePlanSnapshot(
  raw: unknown,
  fallback?: Partial<BillingSubscriptionChangeRequest>,
): BillingPlanSnapshot | null {
  const rec = asRecord(raw)
  if (!rec && !fallback) return null
  const src = rec ?? {}
  const planId = asNumber(src.planId ?? src.id, fallback?.planId ?? 0)
  const planCode = asString(src.planCode ?? src.code)
  const operatorSeats = asNumber(src.operatorSeats, fallback?.operatorSeats ?? 0)
  const periodMonths = asNumber(src.periodMonths ?? src.months, fallback?.periodMonths ?? 0)
  if (!planId && !planCode && !operatorSeats && !periodMonths) return null
  return {
    planId,
    planCode,
    planName: asString(src.planName ?? src.name, planCode),
    operatorSeats,
    periodMonths,
    unitPriceCents: asNumber(src.unitPriceCents ?? src.monthlyOperatorPriceCents),
    discountBps: asNumber(src.discountBps),
    subtotalCents: asNumber(src.subtotalCents),
    discountCents: asNumber(src.discountCents),
    periodAmountCents: asNumber(src.periodAmountCents ?? src.amountCents ?? src.totalCents),
    currency: asString(src.currency, 'USD') || 'USD',
  }
}

function parseInvoiceSummary(raw: unknown): BillingInvoiceSummary | null {
  const rec = asRecord(raw)
  if (!rec) return null
  const id = asId(rec.id ?? rec.invoiceId)
  if (!id) return null
  const numberValue = rec.number ?? rec.invoiceNumber ?? id
  const totalCents = asNumber(rec.totalCents ?? rec.amountCents ?? rec.amountDueCents)
  return {
    id,
    number: String(numberValue),
    status: asString(rec.status, 'PENDING'),
    type: asString(rec.type),
    currency: asString(rec.currency, 'USD') || 'USD',
    totalCents,
    amountDueCents: asNumber(rec.amountDueCents, totalCents),
    dueAt: asNullableString(rec.dueAt ?? rec.dueDate ?? rec.due),
    createdAt: asNullableString(rec.createdAt ?? rec.issuedAt),
    paidAt: asNullableString(rec.paidAt),
  }
}

/**
 * `BILLING_CHANGE_REQUOTE_REQUIRED`: the unused-period credit behind an upgrade invoice has
 * expired (30 min after the quote, or the paid period ended). Re-requesting the same invoice
 * never recalculates it — the user must pick the plan again and get a fresh quote.
 */
export function isRequoteRequiredError(err: unknown): err is BillingApiError {
  return err instanceof BillingApiError && (
    err.code === 'BILLING_CHANGE_REQUOTE_REQUIRED' ||
    /credit has expired|create a new quote/i.test(err.message)
  )
}

/**
 * `POST …/payments` → 409 `BILLING_PAYMENT_ATTEMPT_LIMIT`: the backend caps CREATED/PENDING attempts
 * per invoice. Nothing on this invoice can be retried — the user has to cancel it and get a new one.
 */
export function isPaymentAttemptLimitError(err: unknown): err is BillingApiError {
  return err instanceof BillingApiError && (
    err.code === 'BILLING_PAYMENT_ATTEMPT_LIMIT' ||
    /too many pending payments/i.test(err.message)
  )
}

export function isChangeApplied(status: string | undefined): boolean {
  const normalized = (status || '').trim().toUpperCase()
  return normalized === 'APPLIED' || normalized === 'COMPLETED' || normalized === 'CONFIRMED'
}

export function isQuotedChange(status: string | undefined): boolean {
  return (status || '').trim().toUpperCase() === 'QUOTED'
}

export function isQuoteExpired(change: BillingSubscriptionChange, now = Date.now()): boolean {
  if (change.quoteExpired) return true
  if (!change.expiresAt) return false
  const expires = new Date(change.expiresAt).getTime()
  return Number.isFinite(expires) && expires <= now
}

export function isPendingPaymentChange(status: string | undefined): boolean {
  return (status || '').trim().toUpperCase() === 'PENDING_PAYMENT'
}

export function invoiceNeedsPayment(status: string | undefined): boolean {
  const normalized = (status || '').trim().toUpperCase()
  return normalized === 'PENDING' || normalized === 'OVERDUE' || normalized === 'DRAFT'
}

export function parseBillingSubscriptionChange(
  json: unknown,
  fallback?: Partial<BillingSubscriptionChangeRequest>,
): BillingSubscriptionChange {
  const payload = unwrapBillingData(json)
  const rec = asRecord(payload)
  const nested = asRecord(rec?.change) ?? rec
  if (!nested) {
    throw new BillingApiError(t('errors.billing.parse_plan_change'), 500, 'BILLING_CHANGE_PARSE_FAILED')
  }

  const changeId = asId(nested.changeId ?? nested.id ?? rec?.changeId ?? rec?.id)
  if (!changeId) {
    throw new BillingApiError(t('errors.billing.no_plan_change_id'), 500, 'BILLING_CHANGE_ID_MISSING')
  }

  const to =
    parsePlanSnapshot(nested.to, fallback) ??
    parsePlanSnapshot(nested, fallback)
  const from = parsePlanSnapshot(nested.from)
  const invoice = parseInvoiceSummary(nested.invoice ?? rec?.invoice)
  const amountDueCents = asNumber(
    nested.amountDueCents ?? nested.amountCents ?? invoice?.amountDueCents ?? to?.periodAmountCents,
  )
  const refundDueCents = asNumber(nested.refundDueCents)
  const status = asString(nested.status)

  return {
    changeId,
    subscriptionId: asId(nested.subscriptionId) || null,
    invoiceId: asId(nested.invoiceId ?? invoice?.id) || null,
    status,
    quoteExpired: asBoolean(nested.quoteExpired, false),
    from,
    to,
    creditCents: asNumber(nested.creditCents),
    amountDueCents,
    refundDueCents,
    paymentRequired: asBoolean(nested.paymentRequired, amountDueCents > 0 && !isChangeApplied(status)),
    refundRequired: asBoolean(nested.refundRequired, refundDueCents > 0),
    expiresAt: asNullableString(nested.expiresAt),
    createdAt: asNullableString(nested.createdAt),
    appliedAt: asNullableString(nested.appliedAt),
    invoice,
  }
}

export function mergeSubscriptionChange(
  previous: BillingSubscriptionChange | null | undefined,
  next: BillingSubscriptionChange,
): BillingSubscriptionChange {
  if (!previous) return next
  return {
    ...previous,
    ...next,
    from: next.from ?? previous.from,
    to: next.to ?? previous.to,
    invoice: next.invoice ?? previous.invoice,
    invoiceId: next.invoiceId || previous.invoiceId,
    subscriptionId: next.subscriptionId || previous.subscriptionId,
  }
}

function parseInvoiceListItem(raw: unknown): BillingInvoiceListItem | null {
  const rec = asRecord(raw)
  if (!rec) return null
  const id = asId(rec.id ?? rec.invoiceId)
  if (!id) return null
  const plan = asRecord(rec.plan)
  const change = asRecord(rec.change)
  const to = asRecord(change?.to)
  const totals = asRecord(rec.pricing ?? rec.totals)
  const totalCents = asNumber(rec.totalCents ?? rec.amountCents ?? rec.amountDueCents ?? totals?.totalCents)
  const issuedAt = asNullableString(rec.issuedAt ?? rec.createdAt ?? rec.date)
  return {
    id,
    number: String(rec.number ?? rec.invoiceNumber ?? id),
    status: asString(rec.status, 'PENDING'),
    type: asString(rec.type),
    currency: asString(rec.currency, 'USD') || 'USD',
    subtotalCents: asNumber(rec.subtotalCents ?? totals?.subtotalCents ?? to?.subtotalCents),
    discountCents: asNumber(rec.discountCents ?? totals?.discountCents ?? to?.discountCents),
    totalCents,
    creditCents: asNumber(rec.creditCents ?? totals?.creditCents),
    amountDueCents: asNumber(rec.amountDueCents ?? totals?.amountDueCents, totalCents),
    dueAt: asNullableString(rec.dueAt ?? rec.dueDate ?? rec.due),
    issuedAt,
    paidAt: asNullableString(rec.paidAt),
    createdAt: asNullableString(rec.createdAt ?? issuedAt),
    planCode: asString(rec.planCode ?? plan?.code ?? to?.planCode),
    planName: asString(rec.planName ?? plan?.name ?? to?.planName ?? to?.planCode),
    operatorSeats: asNumber(rec.operatorSeats ?? to?.operatorSeats ?? rec.seats),
    periodMonths: asNumber(rec.periodMonths ?? to?.periodMonths ?? rec.months),
    unitPriceCents: asNumber(
      rec.unitPriceCents ?? to?.unitPriceCents ?? rec.monthlyOperatorPriceCents,
    ),
  }
}

function parseInvoiceLineItem(raw: unknown, index: number): BillingInvoiceLineItem | null {
  const rec = asRecord(raw)
  if (!rec) return null
  const description = asString(rec.description ?? rec.title ?? rec.name ?? rec.label)
  const amountCents = asNumber(rec.amountCents ?? rec.totalCents ?? rec.lineTotalCents)
  const quantity = asNumber(rec.quantity ?? rec.qty, 1)
  const unitPriceCents = asNumber(rec.unitPriceCents ?? rec.priceCents, quantity ? Math.round(amountCents / quantity) : amountCents)
  return {
    id: asId(rec.id) || `line-${index}`,
    description: description || `Позиция ${index + 1}`,
    quantity,
    unitPriceCents,
    amountCents,
  }
}

function parseSnapshotStrings(raw: unknown): Record<string, string> {
  const rec = asRecord(raw)
  if (!rec) return {}
  const out: Record<string, string> = {}
  for (const [key, value] of Object.entries(rec)) {
    if (value == null || value === '') continue
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
      out[key] = String(value)
    }
  }
  return out
}

function parsePagination(raw: unknown, itemCount: number, fallbackPage = 1, fallbackPerPage = 20): BillingPagination {
  const rec = asRecord(raw) ?? {}
  const page = asNumber(rec.page, fallbackPage)
  const perPage = asNumber(rec.perPage ?? rec.limit, fallbackPerPage)
  const total = asNumber(rec.total ?? rec.totalCount, itemCount)
  const totalPages = asNumber(
    rec.totalPages ?? rec.lastPage,
    Math.max(1, Math.ceil((total || itemCount) / (perPage || 1))),
  )
  return {
    page,
    perPage,
    total,
    totalPages,
    hasNextPage: asBoolean(rec.hasNextPage ?? rec.hasMore, page < totalPages),
  }
}

export function parseBillingInvoiceList(json: unknown, query?: BillingInvoiceListQuery): BillingInvoiceList {
  const payload = unwrapBillingData(json)
  const rec = asRecord(payload)
  const rawItems = Array.isArray(rec?.items)
    ? rec.items
    : Array.isArray(payload)
      ? payload
      : extractUnknownList(payload)
  const items = rawItems
    .map(parseInvoiceListItem)
    .filter((item): item is BillingInvoiceListItem => item != null)
  return {
    items,
    pagination: parsePagination(rec?.pagination, items.length, query?.page ?? 1, query?.perPage ?? 20),
  }
}

export function parseBillingPayment(
  json: unknown,
  fallbackInvoiceId?: string,
): BillingPayment {
  const payload = unwrapBillingData(json)
  const rec = asRecord(payload)
  const nested = asRecord(rec?.payment) ?? rec
  if (!nested) {
    throw new BillingApiError(t('errors.billing.parse_payment'), 500, 'BILLING_PAYMENT_PARSE_FAILED')
  }
  const paymentId = asId(nested.paymentId ?? nested.id)
  if (!paymentId) {
    throw new BillingApiError(t('errors.billing.no_payment_id'), 500, 'BILLING_PAYMENT_ID_MISSING')
  }
  let change: BillingSubscriptionChange | null = null
  if (nested.change != null) {
    try {
      change = parseBillingSubscriptionChange({ data: nested.change })
    } catch {
      change = null
    }
  }
  return {
    paymentId,
    invoiceId: asId(nested.invoiceId ?? fallbackInvoiceId),
    provider: asString(nested.provider),
    method: asString(nested.method),
    option: asString(nested.paymentOption ?? nested.option ?? nested.payment_option),
    displayMethod: asNullableString(nested.displayMethod ?? nested.display_method)?.trim().toUpperCase() ?? null,
    status: asString(nested.status, 'PENDING'),
    amountCents: asNumber(nested.amountCents ?? nested.totalCents),
    currency: asString(nested.currency, 'USD') || 'USD',
    checkoutUrl: asNullableString(
      nested.checkoutUrl ??
        nested.redirectUrl ??
        nested.paymentUrl ??
        nested.payUrl ??
        nested.url,
    ),
    paidAt: asNullableString(nested.paidAt),
    createdAt: asNullableString(nested.createdAt),
    invoice: parseInvoiceSummary(nested.invoice),
    change,
  }
}

function parseInvoicePaymentOption(
  raw: unknown,
  fallbackAmountCents = 0,
  fallbackSettingsVersion = 1,
): BillingInvoicePaymentOption | null {
  const rec = asRecord(raw)
  if (!rec) return null
  // `provider` is optional: the backend will drop it from the route, tiles are keyed by option then.
  const provider = asString(rec.provider)
  const option = asString(rec.option ?? rec.optionCode ?? rec.code)
  if (!option) return null
  const invoiceAmountCents = asNumber(
    rec.invoiceAmountCents ?? rec.invoice_amount_cents,
    fallbackAmountCents,
  )
  const feeCents = asNumber(rec.feeCents ?? rec.fee_cents)
  const totalCents = asNumber(rec.totalCents ?? rec.total_cents, invoiceAmountCents + feeCents)
  return {
    provider,
    option,
    title: asString(rec.title ?? rec.name, option) || option,
    method: asString(rec.method, 'PROVIDER').toUpperCase() || 'PROVIDER',
    displayMethod: (asNullableString(rec.displayMethod ?? rec.display_method)?.trim().toUpperCase() ?? null) as BillingInvoicePaymentOption['displayMethod'],
    displayGroup: (asNullableString(rec.displayGroup ?? rec.display_group)?.trim().toLowerCase() ?? null) as BillingInvoicePaymentOption['displayGroup'],
    icon: asString(rec.icon).trim() || null,
    position: Math.max(0, Math.min(2147483647, Math.trunc(asNumber(rec.position)))),
    currency: (asString(rec.currency, 'USD') || 'USD').toUpperCase(),
    unitsPerUsd: asString(rec.unitsPerUsd ?? rec.units_per_usd, '1') || '1',
    decimals: Math.max(0, asNumber(rec.decimals, 2)),
    invoiceAmountCents,
    feeCents,
    totalCents,
    payAmount: asString(rec.payAmount ?? rec.pay_amount ?? rec.amount, String(totalCents)) || String(totalCents),
    feePayer: (asString(rec.feePayer ?? rec.fee_payer, 'SERVICE') || 'SERVICE').toUpperCase(),
    settingsVersion: Math.max(
      1,
      Math.trunc(
        asNumber(
          rec.settingsVersion ?? rec.settings_version ?? rec.version,
          fallbackSettingsVersion,
        ),
      ),
    ),
  }
}

export function parseBillingInvoicePaymentOptions(
  json: unknown,
  fallbackInvoiceId: string,
): BillingInvoicePaymentOptions {
  const payload = unwrapBillingData(json)
  const rec = asRecord(payload) ?? {}
  const amountCents = asNumber(
    rec.amountCents ?? rec.amount_cents ?? rec.totalCents ?? rec.total_cents,
  )
  const currency = (asString(rec.currency, 'USD') || 'USD').toUpperCase()
  const invoiceId = asId(rec.invoiceId ?? rec.invoice_id) || fallbackInvoiceId

  const flat = (() => {
    if (Array.isArray(payload)) return payload
    for (const key of ['options', 'items', 'paymentOptions', 'payment_options']) {
      const value = rec[key]
      if (Array.isArray(value)) return value
    }
    return []
  })()

  const fromFlat = flat
    .map((item) => parseInvoicePaymentOption(item, amountCents))
    .filter((item): item is BillingInvoicePaymentOption => item != null)

  if (fromFlat.length > 0) {
    return { invoiceId, currency, amountCents, options: fromFlat }
  }

  // Nested: providers[{ code, version, options: [...] }]
  const providersRaw = (() => {
    for (const key of ['providers', 'paymentProviders', 'payment_providers']) {
      const value = rec[key]
      if (Array.isArray(value)) return value
    }
    return []
  })()

  const fromProviders: BillingInvoicePaymentOption[] = []
  for (const rawProvider of providersRaw) {
    const providerRec = asRecord(rawProvider)
    if (!providerRec) continue
    const providerCode = asString(providerRec.code ?? providerRec.provider ?? providerRec.id)
    if (!providerCode) continue
    const settingsVersion = Math.max(
      1,
      Math.trunc(asNumber(providerRec.settingsVersion ?? providerRec.version, 1)),
    )
    const opts = Array.isArray(providerRec.options)
      ? providerRec.options
      : Array.isArray(providerRec.methods)
        ? providerRec.methods
        : []
    for (const rawOpt of opts) {
      const optRec = asRecord(rawOpt)
      const merged = {
        ...(optRec ?? {}),
        provider: asString(optRec?.provider, providerCode) || providerCode,
      }
      const parsed = parseInvoicePaymentOption(merged, amountCents, settingsVersion)
      if (parsed) fromProviders.push(parsed)
    }
  }

  return { invoiceId, currency, amountCents, options: fromProviders }
}

function parsePaymentsBlock(raw: unknown, fallbackPage = 1, fallbackPerPage = 20): BillingPaymentsBlock {
  if (Array.isArray(raw)) {
    const items = raw
      .map((item) => {
        try {
          return parseBillingPayment(item)
        } catch {
          return null
        }
      })
      .filter((item): item is BillingPayment => item != null)
    const pagination = parsePagination(null, items.length, fallbackPage, fallbackPerPage)
    return {
      items,
      total: pagination.total,
      limit: pagination.perPage,
      hasMore: pagination.hasNextPage,
      pagination,
    }
  }
  const rec = asRecord(raw) ?? {}
  const list = Array.isArray(rec.items) ? rec.items : []
  const items = list
    .map((item) => {
      try {
        return parseBillingPayment(item)
      } catch {
        return null
      }
    })
    .filter((item): item is BillingPayment => item != null)
  const pagination = parsePagination(
    rec.pagination ?? rec,
    items.length,
    asNumber(rec.page, fallbackPage),
    asNumber(rec.perPage ?? rec.limit, fallbackPerPage),
  )
  return {
    items,
    total: asNumber(rec.total, pagination.total),
    limit: asNumber(rec.limit, pagination.perPage),
    hasMore: asBoolean(rec.hasMore, pagination.hasNextPage),
    pagination,
  }
}

export function parseBillingPaymentsList(
  json: unknown,
  query?: BillingPaymentsListQuery,
): BillingPaymentsList {
  const payload = unwrapBillingData(json)
  const rec = asRecord(payload)
  const nested = asRecord(rec?.payments) ?? rec
  const block = parsePaymentsBlock(nested ?? payload, query?.page ?? 1, query?.perPage ?? 20)
  return {
    items: block.items,
    pagination: block.pagination,
  }
}

export function parseBillingInvoiceDetail(json: unknown): BillingInvoiceDetail {
  const payload = unwrapBillingData(json)
  const rec = asRecord(payload)
  const nested = asRecord(rec?.invoice) ?? rec
  const item = parseInvoiceListItem(nested)
  if (!item) {
    throw new BillingApiError(t('errors.billing.parse_invoice'), 500, 'BILLING_INVOICE_PARSE_FAILED')
  }
  const rawItems = nested?.items ?? rec?.items
  const lineSource = Array.isArray(rawItems) ? rawItems : extractUnknownList(rawItems)
  let change: BillingSubscriptionChange | null = null
  if (nested?.change != null || rec?.change != null) {
    try {
      change = parseBillingSubscriptionChange({ data: nested?.change ?? rec?.change })
    } catch {
      change = null
    }
  }
  return {
    ...item,
    billingProfileSnapshot: parseSnapshotStrings(nested?.billingProfileSnapshot ?? rec?.billingProfileSnapshot),
    items: lineSource
      .map(parseInvoiceLineItem)
      .filter((line): line is BillingInvoiceLineItem => line != null),
    payments: parsePaymentsBlock(nested?.payments ?? rec?.payments),
    change,
  }
}

function parseBillingSubscription(raw: unknown): BillingSubscription | null {
  const subscription = asRecord(raw)
  if (!subscription) return null
  return {
    id: asString(subscription.id),
    status: asString(subscription.status),
    active: asBoolean(subscription.active),
    startedAt: asNullableString(subscription.startedAt),
    currentPeriodStart: asNullableString(subscription.currentPeriodStart),
    currentPeriodEnd: asNullableString(subscription.currentPeriodEnd),
    trialEndsAt: asNullableString(subscription.trialEndsAt),
    accessUntil: asNullableString(subscription.accessUntil),
    autoRenew: asBoolean(subscription.autoRenew),
    cancelAtPeriodEnd: asBoolean(subscription.cancelAtPeriodEnd),
    cancelledAt: asNullableString(subscription.cancelledAt),
    planId: asNullableNumber(subscription.planId ?? subscription.plan_id),
    operatorSeats: positiveInt(subscription.operatorSeats ?? subscription.operator_seats ?? subscription.seats),
    periodMonths: positiveInt(subscription.periodMonths ?? subscription.period_months ?? subscription.months),
  }
}

function parseBillingPlan(raw: unknown): BillingPlan | null {
  const plan = asRecord(raw)
  if (!plan) return null
  return {
    id: asNumber(plan.id),
    code: asString(plan.code),
    name: asString(plan.name),
    kind: asString(plan.kind),
    periodMonths: asNumber(plan.periodMonths),
    unitPriceCents: asNumber(plan.unitPriceCents ?? plan.monthlyOperatorPriceCents),
    discountBps: asNumber(plan.discountBps),
    periodAmountCents: asNumber(plan.periodAmountCents),
    operatorSeats: positiveInt(plan.operatorSeats ?? plan.operator_seats ?? plan.seats) ?? 0,
  }
}

function parseBillingUsage(raw: unknown, limits: BillingLimits | null): BillingUsage | null {
  const usage = asRecord(raw)
  if (!usage) return null
  const regionsUsage = asRecord(usage.regions) ?? {}
  const templateSections = asRecord(usage.templateSections) ?? {}
  return {
    operatorSeats: parseUsageCounter(usage.operatorSeats),
    channels: parseUsageCounter(usage.channels),
    regions: {
      limitPerChannel: asNullableNumber(regionsUsage.limitPerChannel ?? limits?.regionLimit),
      items: Array.isArray(regionsUsage.items)
        ? (regionsUsage.items as unknown[])
            .map(parseRegionUsageItem)
            .filter((item): item is BillingRegionUsageItem => item != null)
        : [],
    },
    templateSections: {
      common: parseUsageCounter(templateSections.common),
      personal: parseUsageCounter(templateSections.personal),
    },
  }
}

function parseBillingAccess(raw: unknown): BillingAccess | null {
  const access = asRecord(raw)
  if (!access) return null
  return {
    state: asString(access.state),
    reason: asNullableString(access.reason),
  }
}

function parseBillingOwner(raw: unknown): BillingOwner | null {
  const owner = asRecord(raw)
  if (!owner) return null
  return {
    id: asNumber(owner.id),
    email: asString(owner.email),
    displayName: asString(owner.displayName ?? owner.display_name),
  }
}

function parseBillingPermissions(raw: unknown, userRole: string): BillingPermissions {
  const permissions = asRecord(raw)
  if (!permissions) {
    const isOperator = userRole.trim().toLowerCase() === 'operator'
    return { canUseWorkspace: true, canManageBilling: !isOperator }
  }
  return {
    canUseWorkspace: asBoolean(permissions.canUseWorkspace, false),
    canManageBilling: asBoolean(permissions.canManageBilling, false),
  }
}

export function parseBillingEntitlements(json: unknown): BillingEntitlements {
  const root = asRecord(json)
  const data = asRecord(root?.data) ?? root
  if (!data) {
    throw new BillingApiError('Failed to load billing entitlements', 500, 'BILLING_ENTITLEMENTS_LOAD_FAILED')
  }

  const user = asRecord(data.user) ?? {}
  const role = asString(user.role)
  const hasLimits = data.limits != null || data.capabilities != null
  const limits = hasLimits ? parseBillingLimits(data.limits, data.capabilities) : null
  const capabilities =
    data.capabilities != null || hasLimits
      ? parseBillingCapabilities(
          data.capabilities,
          limits ?? parseBillingLimits({}, data.capabilities),
        )
      : null

  return {
    generatedAt: asString(data.generatedAt),
    user: {
      id: asNumber(user.id),
      role,
      ownerId: asNullableNumber(user.ownerId),
      emailVerified: asOptionalBoolean(user.emailVerified ?? user.email_verified),
    },
    access: parseBillingAccess(data.access),
    owner: parseBillingOwner(data.owner),
    permissions: parseBillingPermissions(data.permissions, role),
    subscription: parseBillingSubscription(data.subscription),
    plan: parseBillingPlan(data.plan),
    limits,
    usage: parseBillingUsage(data.usage, limits),
    capabilities,
  }
}

const ALLOWED_ACCESS_STATES = new Set(['allowed', 'active', 'ok', 'granted', 'available'])

export function getBillingAccessState(entitlements: BillingEntitlements | null | undefined): string {
  return String(entitlements?.access?.state ?? '').trim().toLowerCase()
}

export function isEntitlementsEmailUnverified(
  entitlements: BillingEntitlements | null | undefined,
): boolean {
  return entitlements?.user.emailVerified === false
}

export function isBillingAccessRestricted(
  entitlements: BillingEntitlements | null | undefined,
): boolean {
  return getBillingAccessState(entitlements) === 'restricted'
}

/** Subscription lock follows `data.access.state`, not `subscription.status`. */
export function isSubscriptionAccessAllowed(entitlements: BillingEntitlements | null | undefined): boolean {
  const state = getBillingAccessState(entitlements)
  if (!state) return true
  return ALLOWED_ACCESS_STATES.has(state)
}

export function canUseWorkspace(entitlements: BillingEntitlements | null | undefined): boolean {
  if (!entitlements) return true
  return entitlements.permissions.canUseWorkspace === true
}

export function canManageBilling(entitlements: BillingEntitlements | null | undefined): boolean {
  return entitlements?.permissions.canManageBilling === true
}

export function billingAccessReason(entitlements: BillingEntitlements | null | undefined): string | null {
  return entitlements?.access?.reason ?? null
}

function billingApiErrorFromBody(data: unknown, status: number): BillingApiError {
  const rec = asRecord(data)
  return new BillingApiError(
    (typeof rec?.error === 'string' && rec.error) ||
      (typeof rec?.message === 'string' && rec.message) ||
      `Request failed (${status})`,
    status,
    typeof rec?.code === 'string' ? rec.code : undefined,
    rec?.details,
  )
}

async function parseError(response: Response): Promise<BillingApiError> {
  try {
    return billingApiErrorFromBody(await response.json(), response.status)
  } catch {
    return new BillingApiError(`Request failed (${response.status})`, response.status)
  }
}

async function billingFetch(
  apiBaseUrl: string,
  authHeaders: Record<string, string>,
  path: string,
  init?: RequestInit,
): Promise<unknown> {
  const base = trimBase(apiBaseUrl)
  const response = await fetch(`${base}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...authHeaders,
      ...(init?.headers ?? {}),
    },
  })

  if (response.status === 401 && handleUnauthorized(response)) {
    throw new BillingApiError('Unauthorized', 401)
  }

  if (response.status === 402) {
    handleUnauthorized(response)
    throw await parseError(response)
  }

  if (!response.ok) {
    throw await parseError(response)
  }

  if (response.status === 204) return null
  const json = await response.json()
  const root = asRecord(json)
  if (root?.success === false) {
    throw billingApiErrorFromBody(root, response.status)
  }
  return json
}

export type BillingFetchOptions = {
  fresh?: boolean
  /** Alias of `fresh` — keep call sites that pass `{ force: true }`. */
  force?: boolean
}

type CacheBox<T> = {
  key: string
  data: T | null
  promise: Promise<T> | null
}

function cacheKey(apiBaseUrl: string, authHeaders: Record<string, string>, extra = ''): string {
  return `${trimBase(apiBaseUrl)}|${authHeaders.Authorization ?? ''}|${extra}`
}

function buildInvoiceListQuery(query: BillingInvoiceListQuery = {}): string {
  const params = new URLSearchParams()
  if (query.page != null) params.set('page', String(query.page))
  if (query.perPage != null) params.set('perPage', String(query.perPage))
  if (query.status) params.set('status', query.status)
  if (query.type) params.set('type', query.type)
  if (query.search) params.set('search', query.search)
  const qs = params.toString()
  return qs ? `?${qs}` : ''
}

function buildPaymentsListQuery(query: BillingPaymentsListQuery = {}): string {
  const params = new URLSearchParams()
  if (query.page != null) params.set('page', String(query.page))
  if (query.perPage != null) params.set('perPage', String(query.perPage))
  const qs = params.toString()
  return qs ? `?${qs}` : ''
}

function peekCache<T>(box: CacheBox<T>, key: string): T | null {
  return box.key === key ? box.data : null
}

function cachedLoad<T>(
  box: CacheBox<T>,
  key: string,
  fresh: boolean,
  loader: () => Promise<T>,
): Promise<T> {
  if (!fresh && box.key === key) {
    if (box.data) return Promise.resolve(box.data)
    if (box.promise) return box.promise
  }
  if (fresh && box.key === key && box.promise) {
    return box.promise
  }

  const promise = loader().then(
    (data) => {
      if (box.promise === promise && box.key === key) {
        box.data = data
        box.promise = null
      }
      return data
    },
    (err) => {
      if (box.promise === promise && box.key === key) {
        box.promise = null
        box.data = null
      }
      throw err
    },
  )

  box.key = key
  box.data = null
  box.promise = promise
  return promise
}

const entitlementsCache: CacheBox<BillingEntitlements> = { key: '', data: null, promise: null }
const plansCache: CacheBox<BillingPlansCatalog> = { key: '', data: null, promise: null }
const invoicesListCache: CacheBox<BillingInvoiceList> = { key: '', data: null, promise: null }
const invoiceDetailCache = new Map<string, BillingInvoiceDetail>()
const invoiceDetailInFlight = new Map<string, Promise<BillingInvoiceDetail>>()
const paymentOptionsInFlight = new Map<string, Promise<BillingInvoicePaymentOptions>>()
const paymentOptionsCache = new Map<string, { at: number; data: BillingInvoicePaymentOptions }>()
const PAYMENT_OPTIONS_CACHE_MS = 5_000

export function peekCachedBillingEntitlements(
  apiBaseUrl: string,
  authHeaders: Record<string, string>,
): BillingEntitlements | null {
  return peekCache(entitlementsCache, cacheKey(apiBaseUrl, authHeaders))
}

export function peekCachedBillingPlans(
  apiBaseUrl: string,
  authHeaders: Record<string, string>,
): BillingPlansCatalog | null {
  return peekCache(plansCache, cacheKey(apiBaseUrl, authHeaders))
}

export function peekCachedBillingInvoices(
  apiBaseUrl: string,
  authHeaders: Record<string, string>,
  query: BillingInvoiceListQuery = {},
): BillingInvoiceList | null {
  return peekCache(invoicesListCache, cacheKey(apiBaseUrl, authHeaders, buildInvoiceListQuery(query)))
}

export function peekCachedBillingInvoice(
  apiBaseUrl: string,
  authHeaders: Record<string, string>,
  invoiceId: string,
): BillingInvoiceDetail | null {
  return invoiceDetailCache.get(cacheKey(apiBaseUrl, authHeaders, invoiceId)) ?? null
}

export function invalidateBillingEntitlementsCache(): void {
  entitlementsCache.key = ''
  entitlementsCache.data = null
  entitlementsCache.promise = null
}

export function invalidateBillingInvoicesCache(): void {
  invoicesListCache.key = ''
  invoicesListCache.data = null
  invoicesListCache.promise = null
  invoiceDetailCache.clear()
  invoiceDetailInFlight.clear()
  paymentOptionsInFlight.clear()
  paymentOptionsCache.clear()
}

export async function fetchBillingEntitlements(
  apiBaseUrl: string,
  authHeaders: Record<string, string>,
  options: BillingFetchOptions = {},
): Promise<BillingEntitlements> {
  const key = cacheKey(apiBaseUrl, authHeaders)
  return cachedLoad(entitlementsCache, key, Boolean(options.fresh || options.force), async () => {
    const json = await billingFetch(apiBaseUrl, authHeaders, BILLING_ENTITLEMENTS_PATH, {
      method: 'GET',
    })
    return parseBillingEntitlements(json)
  })
}

export async function fetchBillingPlans(
  apiBaseUrl: string,
  authHeaders: Record<string, string>,
  options: BillingFetchOptions = {},
): Promise<BillingPlansCatalog> {
  const key = cacheKey(apiBaseUrl, authHeaders)
  return cachedLoad(plansCache, key, Boolean(options.fresh || options.force), async () => {
    const json = await billingFetch(apiBaseUrl, authHeaders, BILLING_PLANS_PATH, { method: 'GET' })
    const catalog = parseBillingPlansCatalog(json)
    if (catalog.plans.length === 0) {
      throw new BillingApiError(t('billing.plans.empty'), 500, 'BILLING_PLANS_EMPTY')
    }
    return catalog
  })
}

export function createBillingIdempotencyKey(prefix?: string): string {
  const id =
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `idemp_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 12)}`
  return prefix ? `${prefix}-${id}` : id
}

export function billingSubscriptionChangePath(changeId: string): string {
  return `/api/billing/subscription/changes/${encodeURIComponent(changeId)}`
}

export function billingSubscriptionChangeConfirmPath(changeId: string): string {
  return `${billingSubscriptionChangePath(changeId)}/confirm`
}

export function billingInvoicePath(invoiceId: string): string {
  return `${BILLING_INVOICES_PATH}/${encodeURIComponent(invoiceId)}`
}

export function billingInvoicePaymentsPath(invoiceId: string): string {
  return `${billingInvoicePath(invoiceId)}/payments`
}

export function billingInvoicePaymentOptionsPath(invoiceId: string): string {
  return `${billingInvoicePath(invoiceId)}/payment-options`
}

export function billingInvoiceManualPaymentPath(invoiceId: string): string {
  return `${billingInvoicePaymentsPath(invoiceId)}/manual`
}

export function billingPaymentPath(paymentId: string): string {
  return `/api/billing/payments/${encodeURIComponent(paymentId)}`
}

export async function requestSubscriptionChange(
  apiBaseUrl: string,
  authHeaders: Record<string, string>,
  body: BillingSubscriptionChangeRequest,
  idempotencyKey?: string,
): Promise<BillingSubscriptionChange> {
  // Fresh key by default — do not reuse keys from cancelled / previous attempts.
  const key = idempotencyKey || createBillingIdempotencyKey()
  const json = await billingFetch(apiBaseUrl, authHeaders, BILLING_SUBSCRIPTION_CHANGE_PATH, {
    method: 'POST',
    headers: {
      'Idempotency-Key': key,
    },
    body: JSON.stringify({
      planId: body.planId,
      operatorSeats: body.operatorSeats,
      periodMonths: body.periodMonths,
      renew: body.renew === true,
    }),
  })
  return parseBillingSubscriptionChange(json, body)
}

export async function fetchSubscriptionChange(
  apiBaseUrl: string,
  authHeaders: Record<string, string>,
  changeId: string,
): Promise<BillingSubscriptionChange> {
  const json = await billingFetch(
    apiBaseUrl,
    authHeaders,
    billingSubscriptionChangePath(changeId),
    { method: 'GET' },
  )
  return parseBillingSubscriptionChange(json)
}

export async function confirmSubscriptionChange(
  apiBaseUrl: string,
  authHeaders: Record<string, string>,
  changeId: string,
  idempotencyKey = createBillingIdempotencyKey('confirm'),
): Promise<BillingSubscriptionChange> {
  const json = await billingFetch(
    apiBaseUrl,
    authHeaders,
    billingSubscriptionChangeConfirmPath(changeId),
    {
      method: 'POST',
      headers: {
        'Idempotency-Key': idempotencyKey,
      },
    },
  )
  if (json == null) {
    return {
      changeId,
      subscriptionId: null,
      invoiceId: null,
      status: 'APPLIED',
      quoteExpired: false,
      from: null,
      to: null,
      creditCents: 0,
      amountDueCents: 0,
      refundDueCents: 0,
      paymentRequired: false,
      refundRequired: false,
      expiresAt: null,
      createdAt: null,
      appliedAt: null,
      invoice: null,
    }
  }
  return parseBillingSubscriptionChange(json)
}

export async function fetchBillingInvoices(
  apiBaseUrl: string,
  authHeaders: Record<string, string>,
  query: BillingInvoiceListQuery = {},
  options: BillingFetchOptions = {},
): Promise<BillingInvoiceList> {
  const qs = buildInvoiceListQuery(query)
  const key = cacheKey(apiBaseUrl, authHeaders, qs)
  return cachedLoad(invoicesListCache, key, Boolean(options.fresh), async () => {
    const json = await billingFetch(apiBaseUrl, authHeaders, `${BILLING_INVOICES_PATH}${qs}`, {
      method: 'GET',
    })
    return parseBillingInvoiceList(json, query)
  })
}

export async function fetchBillingInvoice(
  apiBaseUrl: string,
  authHeaders: Record<string, string>,
  invoiceId: string,
  options: BillingFetchOptions = {},
): Promise<BillingInvoiceDetail> {
  const key = cacheKey(apiBaseUrl, authHeaders, invoiceId)
  if (!options.fresh) {
    const cached = invoiceDetailCache.get(key)
    if (cached) return cached
    const inFlight = invoiceDetailInFlight.get(key)
    if (inFlight) return inFlight
  } else {
    const inFlight = invoiceDetailInFlight.get(key)
    if (inFlight) return inFlight
  }

  const promise = (async () => {
    const json = await billingFetch(apiBaseUrl, authHeaders, billingInvoicePath(invoiceId), {
      method: 'GET',
    })
    return parseBillingInvoiceDetail(json)
  })()

  invoiceDetailInFlight.set(key, promise)
  try {
    const detail = await promise
    invoiceDetailCache.set(key, detail)
    return detail
  } finally {
    if (invoiceDetailInFlight.get(key) === promise) {
      invoiceDetailInFlight.delete(key)
    }
  }
}

export async function fetchBillingPayment(
  apiBaseUrl: string,
  authHeaders: Record<string, string>,
  paymentId: string,
): Promise<BillingPayment> {
  const json = await billingFetch(apiBaseUrl, authHeaders, billingPaymentPath(paymentId), {
    method: 'GET',
  })
  return parseBillingPayment(json)
}

export async function fetchInvoicePaymentOptions(
  apiBaseUrl: string,
  authHeaders: Record<string, string>,
  invoiceId: string,
): Promise<BillingInvoicePaymentOptions> {
  const key = cacheKey(apiBaseUrl, authHeaders, invoiceId)
  const cached = paymentOptionsCache.get(key)
  if (cached && Date.now() - cached.at < PAYMENT_OPTIONS_CACHE_MS) {
    return cached.data
  }
  const inFlight = paymentOptionsInFlight.get(key)
  if (inFlight) return inFlight

  const promise = (async () => {
    const json = await billingFetch(
      apiBaseUrl,
      authHeaders,
      billingInvoicePaymentOptionsPath(invoiceId),
      { method: 'GET' },
    )
    const data = parseBillingInvoicePaymentOptions(json, invoiceId)
    paymentOptionsCache.set(key, { at: Date.now(), data })
    return data
  })()

  paymentOptionsInFlight.set(key, promise)
  try {
    return await promise
  } finally {
    if (paymentOptionsInFlight.get(key) === promise) {
      paymentOptionsInFlight.delete(key)
    }
  }
}

/**
 * Create a payment attempt for an issued invoice.
 * Body: `{ provider, option, settingsVersion }` e.g. kassify / checkout / 3.
 */
export async function createInvoicePayment(
  apiBaseUrl: string,
  authHeaders: Record<string, string>,
  invoiceId: string,
  body: CreateInvoicePaymentBody,
  idempotencyKey = createBillingIdempotencyKey('payment'),
): Promise<BillingPayment> {
  try {
    const json = await billingFetch(
      apiBaseUrl,
      authHeaders,
      billingInvoicePaymentsPath(invoiceId),
      {
        method: 'POST',
        headers: {
          'Idempotency-Key': idempotencyKey,
        },
        body: JSON.stringify({
          provider: body.provider,
          option: body.option,
          settingsVersion: body.settingsVersion,
        }),
      },
    )
    return parseBillingPayment(json, invoiceId)
  } catch (err) {
    if (isRequoteRequiredError(err)) throw err
    // Attempt limit is a hard stop: falling back to an older pending attempt would send
    // the user to a stale checkout page for a method they did not pick.
    if (isPaymentAttemptLimitError(err)) throw err

    if (err instanceof BillingApiError && err.status === 409) {
      const payments = await fetchInvoicePayments(apiBaseUrl, authHeaders, invoiceId, {
        page: 1,
        perPage: 20,
      })
      const existing =
        payments.items.find((p) => p.status.toUpperCase() === 'PENDING') ??
        payments.items.find((p) => {
          const status = p.status.toUpperCase()
          return status === 'SUCCEEDED' || status === 'PAID'
        })
      if (existing) return existing
    }
    throw err
  }
}

export async function createManualInvoicePayment(
  apiBaseUrl: string,
  authHeaders: Record<string, string>,
  invoiceId: string,
): Promise<BillingPayment> {
  // Always a fresh key per call. Reusing a previous key returns that attempt
  // even when it is already CANCELLED.
  const idempotencyKey = createBillingIdempotencyKey('manual-payment')
  try {
    const json = await billingFetch(
      apiBaseUrl,
      authHeaders,
      billingInvoiceManualPaymentPath(invoiceId),
      {
        method: 'POST',
        headers: {
          'Idempotency-Key': idempotencyKey,
        },
      },
    )
    return parseBillingPayment(json, invoiceId)
  } catch (err) {
    if (err instanceof BillingApiError && err.status === 409) {
      const payments = await fetchInvoicePayments(apiBaseUrl, authHeaders, invoiceId, {
        page: 1,
        perPage: 20,
      })
      const existing =
        payments.items.find((p) => p.status.toUpperCase() === 'PENDING') ??
        payments.items.find((p) => {
          const status = p.status.toUpperCase()
          return status === 'SUCCEEDED' || status === 'PAID'
        })
      if (existing) return existing
      // Never fall back to CANCELLED/FAILED — caller must start a new attempt.
    }
    throw err
  }
}

export async function fetchInvoicePayments(
  apiBaseUrl: string,
  authHeaders: Record<string, string>,
  invoiceId: string,
  query: BillingPaymentsListQuery = {},
): Promise<BillingPaymentsList> {
  const qs = buildPaymentsListQuery({
    page: query.page ?? 1,
    perPage: query.perPage ?? 20,
  })
  const json = await billingFetch(
    apiBaseUrl,
    authHeaders,
    `${billingInvoicePaymentsPath(invoiceId)}${qs}`,
    { method: 'GET' },
  )
  return parseBillingPaymentsList(json, query)
}

/** Cancel pending payment invoice / disable renewal — same backend action. */
export async function cancelBillingInvoice(
  apiBaseUrl: string,
  authHeaders: Record<string, string>,
  invoiceId: string,
): Promise<BillingInvoiceDetail> {
  await billingFetch(apiBaseUrl, authHeaders, BILLING_SUBSCRIPTION_CANCEL_AT_PERIOD_END_PATH, {
    method: 'POST',
  })
  invalidateBillingInvoicesCache()
  invalidateBillingEntitlementsCache()
  return fetchBillingInvoice(apiBaseUrl, authHeaders, invoiceId, { fresh: true })
}

export async function cancelSubscriptionAtPeriodEnd(
  apiBaseUrl: string,
  authHeaders: Record<string, string>,
): Promise<BillingEntitlements> {
  await billingFetch(apiBaseUrl, authHeaders, BILLING_SUBSCRIPTION_CANCEL_AT_PERIOD_END_PATH, {
    method: 'POST',
  })
  invalidateBillingEntitlementsCache()
  invalidateBillingInvoicesCache()
  return fetchBillingEntitlements(apiBaseUrl, authHeaders, { fresh: true })
}

export function isPaidCatalogPlan(plan: BillingCatalogPlan): boolean {
  const kind = plan.kind.toUpperCase()
  const code = plan.code.toUpperCase()
  return kind !== 'TRIAL' && code !== 'TRIAL'
}

export function availablePeriodsForPlan(
  plan: BillingCatalogPlan | null | undefined,
  catalogPeriods: BillingPlanPeriod[] = [],
): BillingPlanPeriod[] {
  const fromPlan = plan?.periods.filter((p) => p.periodMonths > 0) ?? []
  if (fromPlan.length > 0) return fromPlan
  if (catalogPeriods.length > 0) return catalogPeriods
  return DEFAULT_BILLING_PERIODS
}

export function resolvePurchasePlanId(
  plan: BillingCatalogPlan,
  periodMonths: number,
): number {
  const match = plan.periods.find((p) => p.periodMonths === periodMonths && p.planId)
  return match?.planId || plan.id
}

export function findPlanPeriod(
  plan: BillingCatalogPlan | null | undefined,
  periodMonths: number,
  catalogPeriods: BillingPlanPeriod[] = [],
): BillingPlanPeriod | null {
  return (
    plan?.periods.find((p) => p.periodMonths === periodMonths) ||
    catalogPeriods.find((p) => p.periodMonths === periodMonths) ||
    DEFAULT_BILLING_PERIODS.find((p) => p.periodMonths === periodMonths) ||
    null
  )
}

export function quoteCatalogPlan(
  plan: BillingCatalogPlan,
  periodMonths: number,
  operatorSeats: number,
  catalogPeriods: BillingPlanPeriod[] = [],
): BillingQuote {
  const seats = Math.max(1, Math.trunc(operatorSeats) || 1)
  const months = Math.max(1, Math.trunc(periodMonths) || 1)
  const period = findPlanPeriod(plan, months, catalogPeriods)
  const unitPriceCents = period?.unitPriceCents ?? plan.unitPriceCents
  const discountBps = period?.discountBps ?? (plan.periodMonths === months ? plan.discountBps : 0)
  const monthlyPerOperatorCents = Math.round(unitPriceCents * (1 - discountBps / 10_000))
  const monthlyCents = monthlyPerOperatorCents * seats
  const perOperatorPeriodCents =
    period?.periodAmountCents != null && period.periodAmountCents > 0
      ? period.periodAmountCents
      : monthlyPerOperatorCents * months
  const totalCents = perOperatorPeriodCents * seats
  const saveCents = Math.max(0, unitPriceCents * seats * months - totalCents)
  return {
    unitPriceCents,
    discountBps,
    monthlyPerOperatorCents,
    monthlyCents,
    periodMonths: months,
    operatorSeats: seats,
    totalCents,
    saveCents,
  }
}

export function periodMonthsLabel(months: number, catalogLabel?: string | null): string {
  if (catalogLabel && catalogLabel.trim()) return catalogLabel.trim()
  if (months === 12) return t('time.periods.year')
  if (months > 0) return monthsShort(months)
  return '—'
}

export function ruPlural(n: number, one: string, few: string, many: string): string {
  const abs = Math.abs(Math.trunc(n))
  const mod10 = abs % 10
  const mod100 = abs % 100
  if (mod100 >= 11 && mod100 <= 14) return many
  if (mod10 === 1) return one
  if (mod10 >= 2 && mod10 <= 4) return few
  return many
}

export function daysUntil(iso: string | null | undefined, now = new Date()): number | null {
  if (!iso) return null
  const end = new Date(iso)
  if (Number.isNaN(end.getTime())) return null
  return Math.ceil((end.getTime() - now.getTime()) / 86_400_000)
}

export function trialDaysRemaining(entitlements: BillingEntitlements, now = new Date()): number | null {
  const sub = entitlements.subscription
  const end = sub?.trialEndsAt || sub?.accessUntil || sub?.currentPeriodEnd
  return daysUntil(end, now)
}

export function isTrialPlan(entitlements: BillingEntitlements): boolean {
  const kind = (entitlements.plan?.kind ?? '').toUpperCase()
  const status = (entitlements.subscription?.status ?? '').toUpperCase()
  const code = (entitlements.plan?.code ?? '').toUpperCase()
  return kind === 'TRIAL' || status === 'TRIAL' || code === 'TRIAL'
}

/** planId, seats and period of the paid subscription. Renewal must send these unchanged. */
export function currentPaidSubscriptionTerms(
  entitlements: BillingEntitlements | null | undefined,
): Pick<BillingSubscriptionChangeRequest, 'planId' | 'operatorSeats' | 'periodMonths'> | null {
  const plan = entitlements?.plan
  const subscription = entitlements?.subscription
  if (!plan || !subscription || !entitlements || isTrialPlan(entitlements)) return null
  const planId = plan.id || subscription.planId || 0
  const periodMonths = plan.periodMonths || subscription.periodMonths || 0
  const seatLimit = entitlements.usage?.operatorSeats
  const operatorSeats =
    plan.operatorSeats ||
    subscription.operatorSeats ||
    (seatLimit && !seatLimit.unlimited ? seatLimit.limit ?? 0 : 0)
  if (planId <= 0 || periodMonths <= 0 || operatorSeats <= 0) return null
  return { planId, operatorSeats, periodMonths }
}

export function trialLengthLabel(entitlements: BillingEntitlements): string {
  if (!isTrialPlan(entitlements)) return entitlements.plan?.name || entitlements.plan?.code || ''
  const start = entitlements.subscription?.startedAt || entitlements.subscription?.currentPeriodStart
  const end = entitlements.subscription?.trialEndsAt || entitlements.subscription?.accessUntil
  if (start && end) {
    const ms = new Date(end).getTime() - new Date(start).getTime()
    if (Number.isFinite(ms) && ms > 0) {
      const days = Math.max(1, Math.round(ms / 86_400_000))
      return timeUnit('day', days) + ' ' + t('billing.free')
    }
  }
  return t('billing.trial.title')
}

export function subscriptionStatusLabel(entitlements: BillingEntitlements): string {
  const accessState = getBillingAccessState(entitlements)
  if (accessState && !isSubscriptionAccessAllowed(entitlements)) {
    const reason = billingAccessReason(entitlements)
    if (reason) return reason
    if (accessState === 'expired' || accessState === 'blocked') return t('app.status.completed')
    return t('billing.status.restricted')
  }
  const { subscription } = entitlements
  if (!subscription) {
    return isSubscriptionAccessAllowed(entitlements) ? t('app.status.active') : t('app.status.inactive')
  }
  if (isTrialPlan(entitlements)) {
    if (!isSubscriptionAccessAllowed(entitlements) || !subscription.active) return t('app.status.completed')
    const days = trialDaysRemaining(entitlements)
    if (days == null) return t('app.status.active')
    if (days <= 0) return t('billing.subscription.active_last_day')
    return t('app.status.active') + ' · ' + t('billing.trial.remaining_label').toLowerCase() + ' ' + timeUnit('day', days)
  }
  const status = subscription.status.toUpperCase()
  if (status === 'PAST_DUE') return t('billing.invoice.overdue')
  if (status === 'CANCELLED' || status === 'CANCELED') {
    return subscription.cancelAtPeriodEnd && isSubscriptionAccessAllowed(entitlements)
      ? t('billing.subscription.cancel_at_period_end')
      : t('app.status.canceled')
  }
  if (status === 'EXPIRED') return t('app.status.completed')
  const planName = entitlements.plan?.name
  return isSubscriptionAccessAllowed(entitlements)
    ? planName
      ? `${t('app.status.active')} · ${planName}`
      : t('app.status.active')
    : t('app.status.inactive')
}

export function mapPlanCodeToBillingPlanId(code: string): 'start' | 'pro' | 'max' | null {
  const normalized = code.trim().toUpperCase()
  if (normalized === 'START' || normalized === 'STARTER') return 'start'
  if (normalized === 'PRO' || normalized === 'PROFI') return 'pro'
  if (normalized === 'MAX' || normalized === 'MAXIMUM') return 'max'
  return null
}

export function isCustomColorAllowed(level: string | undefined): boolean {
  return (level || '').toUpperCase() !== 'BASIC'
}

export function formatBillingDate(iso: string | null | undefined): string {
  if (!iso) return '—'
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return '—'
  return date.toLocaleDateString('ru-RU', { day: 'numeric', month: 'short', year: 'numeric' })
}

export function formatBillingDateTime(iso: string | null | undefined): string {
  if (!iso) return '—'
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return '—'
  return date.toLocaleString('ru-RU', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export function formatMoneyCents(cents: number, currency = 'USD'): string {
  const amount = cents / 100
  try {
    return new Intl.NumberFormat(currency === 'USD' ? 'en-US' : 'ru-RU', {
      style: 'currency',
      currency: currency || 'USD',
      minimumFractionDigits: Number.isInteger(amount) ? 0 : 2,
      maximumFractionDigits: 2,
    }).format(amount)
  } catch {
    return `${amount.toFixed(2)} ${currency}`
  }
}

export function formatCentsUsd(cents: number): string {
  return formatMoneyCents(cents, 'USD')
}

/** Сумма с рублёвым эквивалентом: «$29 / 2 465 ₽» (для USD; остальные валюты — как есть). */
export function formatMoneyCentsDual(cents: number, currency = 'USD'): string {
  return withRub(formatMoneyCents(cents, currency), cents / 100, currency)
}

export function invoiceNumberPlain(number: string | number): string {
  return String(number)
    .trim()
    .replace(/^[№#]\s*/u, '')
}

export function formatInvoiceNumber(number: string | number): string {
  const value = invoiceNumberPlain(number)
  if (!value) return '—'
  return value.startsWith('#') || value.startsWith('№') ? value : `№ ${value}`
}

export type InvoiceUiState = 'pending' | 'processing' | 'paid' | 'cancel' | 'overdue'

export function invoiceUiState(
  invoice: Pick<BillingInvoiceListItem, 'status'> & {
    payments?: BillingPaymentsBlock
  },
): InvoiceUiState {
  const kind = invoiceStatusKind(invoice.status)
  if (kind === 'paid') return 'paid'
  if (kind === 'cancel') return 'cancel'
  if (kind === 'over') return 'overdue'
  const items = invoice.payments?.items ?? []
  const hasPending = items.some((p) => p.status.toUpperCase() === 'PENDING')
  if (hasPending) return 'processing'
  return 'pending'
}

export function invoiceStatusLabel(status: string | undefined): string {
  const normalized = (status || '').trim().toUpperCase()
  if (normalized === 'PAID') return t('billing.invoice.status_paid')
  if (normalized === 'PENDING') return t('app.status.pending')
  if (normalized === 'OVERDUE') return t('billing.invoice.overdue')
  if (normalized === 'DRAFT') return t('billing.invoice.draft')
  if (normalized === 'CANCELLED' || normalized === 'CANCELED') return t('app.status.canceled')
  if (normalized === 'REFUNDED') return t('billing.refund')
  if (normalized === 'PARTIALLY_REFUNDED') return t('billing.invoice.partial_refund')
  return status || '—'
}

export function invoiceStatusKind(
  status: string | undefined,
): 'paid' | 'wait' | 'over' | 'cancel' {
  const normalized = (status || '').trim().toUpperCase()
  if (normalized === 'PAID' || normalized === 'REFUNDED' || normalized === 'PARTIALLY_REFUNDED') {
    return 'paid'
  }
  if (normalized === 'OVERDUE') return 'over'
  if (normalized === 'CANCELLED' || normalized === 'CANCELED') return 'cancel'
  return 'wait'
}

export function invoiceTypeLabel(type: string | undefined): string {
  const normalized = (type || '').trim().toUpperCase()
  if (normalized === 'NEW_SUBSCRIPTION') return t('billing.subscription.new')
  if (normalized === 'RENEWAL') return t('billing.renewal.title')
  if (normalized === 'PLAN_CHANGE') return t('billing.plan_change.title')
  if (normalized === 'SEAT_CHANGE') return t('billing.seats.change')
  if (normalized === 'CANCELLATION') return t('app.actions.cancel')
  if (normalized === 'ADJUSTMENT') return t('billing.invoice.adjustment')
  return type || ''
}

export function paymentStatusLabel(status: string | undefined): string {
  const normalized = (status || '').trim().toUpperCase()
  if (normalized === 'SUCCEEDED' || normalized === 'PAID') return t('billing.invoice.status_paid')
  if (normalized === 'PENDING') return t('app.status.awaiting_confirmation')
  if (normalized === 'FAILED') return t('errors.title')
  if (normalized === 'CANCELLED' || normalized === 'CANCELED') return t('app.status.canceled')
  return status || '—'
}

export function snapshotFieldLabel(key: string): string {
  const map: Record<string, string> = {
    name: t('app.labels.name'),
    companyName: t('app.labels.company'),
    legalName: t('billing.payer.legal_entity'),
    email: 'Email',
    phone: t('app.fields.phone'),
    country: t('chat.client.country'),
    address: t('app.labels.address'),
    taxId: t('billing.details.tax_id_short'),
    vatId: 'VAT',
  }
  return map[key] || key
}

export function formatUsageLabel(counter: BillingUsageCounter): string {
  if (counter.unlimited || counter.limit == null) return `${counter.used} · ${t('billing.limits.unlimited')}`
  return t('app.of_total', { current: counter.used, total: counter.limit })
}

export function usageBarPercent(counter: BillingUsageCounter): number {
  if (counter.unlimited || counter.limit == null || counter.limit <= 0) return counter.used > 0 ? 8 : 0
  return Math.min(100, Math.round((counter.used / counter.limit) * 100))
}

export function formatLimitQuota(limit: number | null | undefined): string {
  if (limit == null) return t('billing.limits.unlimited')
  return String(limit)
}

/**
 * Effective numeric cap from entitlements.
 * `null` means unlimited. Until entitlements are ready, `fallback` is used.
 */
export function effectiveCap(
  entitlementsReady: boolean,
  fallback: number,
  unlimited: boolean | undefined,
  ...limits: Array<number | null | undefined>
): number | null {
  if (!entitlementsReady) return fallback
  if (unlimited) return null
  for (const limit of limits) {
    if (limit != null) return limit
  }
  return null
}

export function isPlanLimitReached(used: number, limit: number | null): boolean {
  return limit != null && used >= limit
}

/** Historical UI: 4 extra regions + default. Used until entitlements load. */
export const FALLBACK_REGION_LIMIT_PER_CHANNEL = 5

export function findChannelRegionUsage(
  entitlements: BillingEntitlements | null | undefined,
  identity: { widgetKey?: string; widgetId?: number },
): BillingRegionUsageItem | undefined {
  if (!entitlements?.usage) return undefined
  const key = identity.widgetKey?.trim()
  const widgetId = identity.widgetId
  return entitlements.usage.regions.items.find((item) => {
    if (key && item.widgetKey && item.widgetKey === key) return true
    if (widgetId != null && (item.widgetId === widgetId || item.channelId === widgetId)) return true
    return false
  })
}

export function customizationLevelLabel(level: string | undefined): string {
  const normalized = (level || '').toUpperCase()
  if (normalized === 'BASIC') return t('billing.features.basic')
  if (normalized === 'ADVANCED') return t('billing.customization.extended')
  if (normalized === 'FULL' || normalized === 'PRO') return t('billing.customization.full')
  return level || '—'
}

export function formatDiscountBps(discountBps: number): string {
  if (!discountBps) return '0%'
  return `${(discountBps / 100).toLocaleString('ru-RU', { maximumFractionDigits: 2 })}%`
}

export type EntitlementFeatureLine = { text: string; tip: string }

/** Seats are sold per subscription, not as an included unlimited pool. */
export function operatorSeatPlanLine(): string {
  return `1 ${t('billing.subscription.word')} - 1 ${t('team.operator_lower')}`
}
export function operatorSeatPlanTip(): string {
  return t('billing.seats.one_per_subscription')
}

/** Feature bullets from plan limits + capabilities. */
export function buildPlanFeatureLines(
  limits: BillingLimits,
  capabilities: Pick<BillingCapabilities, 'personalTemplates' | 'customizationLevel' | 'removeBranding'>,
): EntitlementFeatureLine[] {
  const lines: EntitlementFeatureLine[] = []

  lines.push({
    text:
      limits.channelLimit == null
        ? t('billing.limits.unlimited_channels')
        : t('channels.channels_count', { count: limits.channelLimit }),
    tip: t('billing.limits.channels_hint'),
  })

  lines.push({
    text:
      limits.regionLimit == null
        ? t('billing.limits.unlimited_regions_per_channel')
        : t('billing.limits.up_to_regions_per_channel', { count: limits.regionLimit }),
    tip: t('billing.limits.regions_hint'),
  })

  const common =
    limits.commonTemplateSectionLimit == null
      ? t('billing.limits.unlimited_shared_sections')
      : t('templates.counts.shared_sections', { count: limits.commonTemplateSectionLimit })
  const personalEnabled = capabilities.personalTemplates || limits.personalTemplatesEnabled
  const personal = !personalEnabled
    ? t('billing.limits.no_personal')
    : limits.personalTemplateSectionLimit == null
      ? t('billing.limits.unlimited_personal')
      : t('templates.counts.personal', { count: limits.personalTemplateSectionLimit })
  lines.push({
    text: `${t('nav.templates')}: ${common}${personalEnabled ? ` + ${personal}` : ''}`,
    tip: t('billing.limits.template_sections_hint'),
  })

  lines.push({
    text: `${t('nav.customization')}: ${customizationLevelLabel(capabilities.customizationLevel || limits.customizationLevel)}`,
    tip: t('billing.limits.customization_hint'),
  })

  lines.push({
    text: capabilities.removeBranding || limits.canRemoveBranding
      ? t('billing.features.remove_branding_short')
      : t('billing.features.brand_stays'),
    tip: t('billing.limits.brand_removal_hint'),
  })

  return lines
}

/** Feature bullets for the current plan — only from limits + capabilities. */
export function buildEntitlementFeatureLines(entitlements: BillingEntitlements): EntitlementFeatureLine[] {
  if (!entitlements.limits) return []
  return buildPlanFeatureLines(
    entitlements.limits,
    entitlements.capabilities ?? {
      personalTemplates: entitlements.limits.personalTemplatesEnabled,
      createCommonTemplateSections: true,
      customizationLevel: entitlements.limits.customizationLevel,
      removeBranding: entitlements.limits.canRemoveBranding,
    },
  )
}

export function formatTrialLimitsLine(entitlements: BillingEntitlements): string {
  const { limits, capabilities } = entitlements
  if (!limits) return ''
  const parts: string[] = []

  if (limits.channelLimit == null) parts.push(t('billing.limits.unlimited_channels'))
  else parts.push(t('channels.channels_count', { count: limits.channelLimit }))

  parts.push(operatorSeatPlanLine())

  if (limits.regionLimit == null) parts.push(t('billing.limits.unlimited_regions'))
  else parts.push(t('billing.limits.up_to_regions_per_channel', { count: limits.regionLimit }).toLowerCase())

  parts.push(`${t('nav.customization').toLowerCase()}: ${customizationLevelLabel(capabilities?.customizationLevel || limits.customizationLevel)}`)
  parts.push(
    (capabilities?.personalTemplates || limits.personalTemplatesEnabled)
      ? t('templates.personal_templates_lower')
      : t('billing.limits.no_personal_templates'),
  )
  parts.push(
    (capabilities?.removeBranding || limits.canRemoveBranding)
      ? t('billing.features.remove_branding_short')
      : t('billing.features.brand_stays'),
  )

  return parts.join(' · ')
}
